import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowRight,
  ArrowUpRight,
  Banknote,
  Eye,
  EyeOff,
  ReceiptText,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { RunwayBanner } from '@/components/financials/RunwayBanner';
import { DailyBriefBanner } from '@/components/dashboard/DailyBriefBanner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import {
  dashboardPrivacyApplies,
  readDashboardDefaultVisible,
} from '@/lib/dashboardVisibility';
import { BRAND, CHART_SERIES } from '@/lib/brand';
import { formatCurrency, formatRelative } from '@/lib/formatters';
import {
  useBankAccounts,
  useDailyCashFlow,
  useMonthlyPnl,
  useTransactions,
} from '@/features/financials/hooks';

export function DashboardPage() {
  const { profile, role } = useAuth();
  const canSeeFinancials = canRead(role, 'financials');
  const privacyMode = dashboardPrivacyApplies(role);

  // Session visibility — initialized from the user's default preference.
  // Toggling Show/Hide here does not change the default; that lives in Settings.
  const [visible, setVisible] = useState(() => readDashboardDefaultVisible(role));

  const firstName = profile?.full_name ? profile.full_name.split(' ')[0] : '';

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome back${firstName ? `, ${firstName}` : ''}`}
        actions={
          <div className="flex items-center gap-2">
            {privacyMode && (
              <Button
                variant="outline"
                onClick={() => setVisible((v) => !v)}
                aria-pressed={visible}
              >
                {visible ? (
                  <>
                    <EyeOff className="h-4 w-4" /> Hide dashboard
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" /> Show dashboard
                  </>
                )}
              </Button>
            )}
            {canSeeFinancials && visible && (
              <Button variant="outline" asChild>
                <Link to="/financials/projections?tab=pnl">
                  Open P&L <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>
        }
      />

      {privacyMode && !visible ? (
        <Card>
          <CardHeader>
            <CardTitle>Dashboard hidden</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setVisible(true)}>
              <Eye className="h-4 w-4" /> Show dashboard
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <DailyBriefBanner />
          {canSeeFinancials && <DashboardFinancials />}
        </>
      )}
    </div>
  );
}

/**
 * Mounted only while the snapshot is visible so financial queries (and the
 * runway Edge Function) do not fire during a hidden screenshare load. The
 * daily brief lives above this block and is gated by the same visibility
 * toggle for super_admins.
 */
function DashboardFinancials() {
  const { data: accounts, isLoading: aLoading } = useBankAccounts();
  // Recent activity is a glanceable feed, not an audit ledger — collapse
  // paired internal transfers (CC payoff, bank-to-bank) so a single
  // movement of money shows as one entry. Both halves still exist in
  // the database and reconcile per-account; they just don't visually
  // double-up on the dashboard. See migration 72 for the pairing logic
  // and `useTransactions` for the collapse implementation.
  const { data: txns, isLoading: tLoading } = useTransactions({
    limit: 8,
    collapseTransfers: true,
  });
  const { data: pnl } = useMonthlyPnl();
  const { data: cash } = useDailyCashFlow({ days: 60 });

  // Mercury credit lines (account_type = 'credit') represent debt, not
  // cash, so exclude them from "Cash on hand". The Accounts page surfaces
  // the credit balance separately.
  const depositAccounts = useMemo(
    () => (accounts ?? []).filter((a) => !(a.account_type ?? '').toLowerCase().includes('credit')),
    [accounts],
  );
  const totalCash = useMemo(
    () => depositAccounts.reduce((acc, a) => acc + (a.current_balance_cents ?? 0), 0),
    [depositAccounts],
  );

  const last30Inflow = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return (cash ?? [])
      .filter((d) => d.direction === 'credit' && new Date(d.day).getTime() >= cutoff)
      .reduce((acc, r) => acc + r.amount_cents, 0);
  }, [cash]);

  const last30Outflow = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return (cash ?? [])
      .filter((d) => d.direction === 'debit' && new Date(d.day).getTime() >= cutoff)
      .reduce((acc, r) => acc + r.amount_cents, 0);
  }, [cash]);

  // Aggregate P&L by month for the chart
  const pnlChartData = useMemo(() => {
    const map = new Map<string, { month: string; revenue: number; expense: number }>();
    for (const row of pnl ?? []) {
      const key = row.month.slice(0, 7);
      if (!map.has(key)) map.set(key, { month: key, revenue: 0, expense: 0 });
      const entry = map.get(key)!;
      if (row.account_type === 'revenue') entry.revenue += row.amount_cents / 100;
      else entry.expense += row.amount_cents / 100;
    }
    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [pnl]);

  const cashFlowChartData = useMemo(() => {
    const map = new Map<string, { day: string; inflow: number; outflow: number }>();
    for (const row of cash ?? []) {
      const day = row.day.slice(0, 10);
      if (!map.has(day)) map.set(day, { day, inflow: 0, outflow: 0 });
      const entry = map.get(day)!;
      if (row.direction === 'credit') entry.inflow += row.amount_cents / 100;
      else entry.outflow += row.amount_cents / 100;
    }
    return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [cash]);

  return (
    <>
      <RunwayBanner />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Cash on hand"
          value={formatCurrency(totalCash)}
          icon={<Wallet className="h-4 w-4 text-muted-foreground" />}
          loading={aLoading}
          hint={
            accounts
              ? `${depositAccounts.length} bank account${depositAccounts.length === 1 ? '' : 's'}`
              : undefined
          }
        />
        <MetricCard
          label="Inflow (30d)"
          value={formatCurrency(last30Inflow)}
          icon={<ArrowUpRight className="h-4 w-4 text-bright-moss-dark" />}
          loading={!cash}
        />
        <MetricCard
          label="Outflow (30d)"
          value={formatCurrency(last30Outflow)}
          icon={<Banknote className="h-4 w-4 text-fresh-salmon-dark" />}
          loading={!cash}
        />
        <MetricCard
          label="Net (30d)"
          value={formatCurrency(last30Inflow - last30Outflow)}
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
          loading={!cash}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Revenue vs. Expense</CardTitle>
          </CardHeader>
          <CardContent>
            {pnlChartData.length === 0 ? (
              <Skeleton className="h-64" />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pnlChartData}>
                    <CartesianGrid stroke={BRAND.linenDark} vertical={false} />
                    <XAxis dataKey="month" stroke={BRAND.oldGrowth} fontSize={11} />
                    <YAxis
                      stroke={BRAND.oldGrowth}
                      fontSize={11}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(v: number) => `$${v.toLocaleString()}`}
                      contentStyle={{
                        background: '#fff',
                        border: `1px solid ${BRAND.linenDark}`,
                        borderRadius: 8,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="revenue" fill={CHART_SERIES[0]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expense" fill={CHART_SERIES[4]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
          </CardHeader>
          <CardContent>
            {tLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (txns ?? []).length === 0 ? (
              <EmptyState
                icon={ReceiptText}
                title="No transactions yet"
              />
            ) : (
              <ul className="space-y-3">
                {(txns ?? []).map((t) => {
                  const partner = t.transfer_partner;
                  const ownAccount = t.bank_accounts?.name ?? 'Unknown account';
                  // For paired transfers, render the source line as
                  // "Mercury Checking → Mercury Credit" so the feed shows
                  // the full picture even though we collapsed the
                  // partner row.
                  const sourceLabel = partner
                    ? t.direction === 'debit'
                      ? `${ownAccount} → ${partner.bank_accounts?.name ?? 'Other account'}`
                      : `${partner.bank_accounts?.name ?? 'Other account'} → ${ownAccount}`
                    : ownAccount;
                  return (
                    <li key={t.id} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">
                          {t.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sourceLabel} · {formatRelative(t.posted_at)}
                          {partner && (
                            <span className="ml-1.5 rounded-sm bg-atmosphere-dark/10 px-1 py-0.5 text-[10px] uppercase tracking-wide text-atmosphere-dark">
                              Transfer
                            </span>
                          )}
                        </p>
                      </div>
                      <span
                        className={
                          'shrink-0 tabular-nums text-sm ' +
                          (partner
                            ? 'text-muted-foreground'
                            : t.direction === 'credit'
                              ? 'text-bright-moss-dark'
                              : 'text-foreground')
                        }
                      >
                        {!partner && (t.direction === 'credit' ? '+' : '−')}
                        {formatCurrency(t.amount_cents)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cash flow (60 days)</CardTitle>
        </CardHeader>
        <CardContent>
          {cashFlowChartData.length === 0 ? (
            <Skeleton className="h-64" />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cashFlowChartData}>
                  <CartesianGrid stroke={BRAND.linenDark} vertical={false} />
                  <XAxis dataKey="day" stroke={BRAND.oldGrowth} fontSize={11} />
                  <YAxis
                    stroke={BRAND.oldGrowth}
                    fontSize={11}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(v: number) => `$${v.toLocaleString()}`}
                    contentStyle={{
                      background: '#fff',
                      border: `1px solid ${BRAND.linenDark}`,
                      borderRadius: 8,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="inflow"
                    stroke={CHART_SERIES[2]}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="outflow"
                    stroke={CHART_SERIES[4]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
